// Configuración · usuarios, roles y permisos (docs/108 B10). Un usuario entra por INVITACIÓN (el token
// se muestra una sola vez para entregarlo por un canal seguro); sus roles se asignan por alcance
// (empresa) con motivo; suspender revoca sus sesiones. Los roles editables cambian sus permisos con
// motivo; los permisos sensibles piden motivo en cada uso.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import { PanelError } from '../comun/Estados';
import { fechaHora } from '../comun/formato';
import { Mantenedor, type DefMantenedor } from '../comun/Mantenedor';
import { Modal } from '../comun/Modal';
import { useSesion } from '../sesion/SesionContexto';

interface Usuario {
  id: string;
  nombre: string;
  correo: string;
  estado: string;
  ultimoAccesoEn: string | null;
  creadoEn: string;
}
interface Rol {
  id: string;
  codigo: string;
  nombre: string;
  descripcion: string | null;
  editable: boolean;
  permisos: string[];
}
interface Permiso {
  codigo: string;
  dominio: string;
  descripcion: string;
  sensible: boolean;
  requiere_motivo: boolean;
}

const useRoles = () =>
  useQuery({ queryKey: ['config', 'roles'], queryFn: () => api.get<Rol[]>('/roles') });

export function Usuarios() {
  const { empresa, usuario: yo } = useSesion();
  const empresaId = empresa!.id;
  const roles = useRoles();
  const [token, setToken] = useState<string | null>(null);
  const [para, setPara] = useState<string | null>(null);
  const [roles_de, setRolesDe] = useState<Usuario | null>(null);
  const opcionesRol = (roles.data ?? []).map((r) => ({
    valor: r.codigo,
    texto: `${r.nombre} (${r.codigo})`,
  }));
  return (
    <>
      {token && (
        <div className="sin-fuente" role="alert">
          <b>Enlace de activación{para ? ` de ${para}` : ''} (se muestra UNA vez):</b>
          <span className="mono" data-enlace-invitacion>
            {`${window.location.origin}${window.location.pathname}#/activar?token=${encodeURIComponent(token)}`}
          </span>
          <span>
            Envíaselo a la persona (por ejemplo, por WhatsApp): al abrirlo define SU clave y entra
            con su correo. Nadie más conoce esa clave.
          </span>
          <button
            type="button"
            onClick={() =>
              void navigator.clipboard?.writeText(
                `${window.location.origin}${window.location.pathname}#/activar?token=${encodeURIComponent(token)}`,
              )
            }
          >
            Copiar enlace
          </button>
          <button type="button" onClick={() => setToken(null)}>
            Ya lo entregué
          </button>
        </div>
      )}
      <Mantenedor
        def={
          {
            etiqueta: 'Usuarios',
            claveConsulta: ['config', 'usuarios'],
            listar: () => api.get<Usuario[]>('/usuarios'),
            clave: (u) => u.id,
            textoDe: (u) => `${u.nombre} ${u.correo}`,
            columnas: [
              { id: 'n', titulo: 'Nombre', celda: (u) => u.nombre },
              { id: 'c', titulo: 'Correo', celda: (u) => u.correo },
              { id: 'e', titulo: 'Estado', celda: (u) => u.estado, ancho: '7rem' },
              {
                id: 'a',
                titulo: 'Último acceso',
                celda: (u) => fechaHora(u.ultimoAccesoEn),
                ancho: '10rem',
              },
            ],
            campos: [
              { id: 'nombre', etiqueta: 'Nombre', requerido: true },
              {
                id: 'correo',
                etiqueta: 'Correo',
                requerido: true,
                patron: /^[^@\s]+@[^@\s]+\.[^@\s]+$/,
              },
              {
                id: 'rolCodigo',
                etiqueta: 'Rol inicial',
                tipo: 'seleccion',
                requerido: true,
                opciones: opcionesRol,
              },
            ],
            crear: (v) => ({
              ruta: '/usuarios/invitaciones',
              cuerpo: {
                nombre: v['nombre'],
                correo: v['correo'],
                rolCodigo: v['rolCodigo'],
                scope: { empresaId },
              },
            }),
            alCreado: (r) => {
              const tk = (r as { unaVez?: { token?: string } } | null)?.unaVez?.token;
              if (tk) {
                setToken(tk);
                setPara(null);
              }
            },
            permisoCrear: 'organizacion.usuarios.crear',
            acciones: [
              {
                texto: 'Roles',
                permiso: 'organizacion.roles.gestionar',
                abrir: (u) => setRolesDe(u),
              },
              {
                texto: 'Suspender',
                conMotivo: true,
                permiso: 'organizacion.usuarios.suspender',
                visible: (u) => u.estado === 'activo',
                pedido: (u, motivo) => ({
                  ruta: `/usuarios/${u.id}/suspender`,
                  cuerpo: { motivo },
                }),
              },
              {
                // Olvidó su clave (2026-10-25): enlace nuevo de un solo uso; nadie conoce su clave.
                texto: 'Restablecer acceso',
                conMotivo: true,
                permiso: 'organizacion.usuarios.crear',
                visible: (u) => u.estado === 'activo' && u.id !== yo?.id,
                pedido: (u, motivo) => ({
                  ruta: `/usuarios/${u.id}/restablecer`,
                  cuerpo: { motivo },
                }),
                alHecho: (r, u) => {
                  const tk = (r as { unaVez?: { token?: string } } | null)?.unaVez?.token;
                  if (tk) {
                    setToken(tk);
                    setPara(`${u.nombre} (${u.correo})`);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                },
              },
              {
                texto: 'Reactivar',
                conMotivo: true,
                permiso: 'organizacion.usuarios.suspender',
                visible: (u) => u.estado === 'suspendido',
                pedido: (u, motivo) => ({
                  ruta: `/usuarios/${u.id}/reactivar`,
                  cuerpo: { motivo },
                }),
              },
              {
                texto: 'Reinvitar',
                permiso: 'organizacion.usuarios.crear',
                visible: (u) => u.estado === 'invitado',
                pedido: (u) => ({ ruta: `/usuarios/${u.id}/reinvitar`, cuerpo: {} }),
                // El enlace nuevo se muestra arriba (2026-10-25: «aprieto reinvitar y no pasa nada»).
                alHecho: (r, u) => {
                  const tk = (r as { unaVez?: { token?: string } } | null)?.unaVez?.token;
                  if (tk) {
                    setToken(tk);
                    setPara(`${u.nombre} (${u.correo})`);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                },
              },
            ],
          } satisfies DefMantenedor<Usuario>
        }
      />
      {roles_de && (
        <RolesDeUsuario
          usuario={roles_de}
          roles={roles.data ?? []}
          alCerrar={() => setRolesDe(null)}
        />
      )}
    </>
  );
}

function RolesDeUsuario({
  usuario,
  roles,
  alCerrar,
}: {
  usuario: Usuario;
  roles: Rol[];
  alCerrar: () => void;
}) {
  const { empresa, empresas } = useSesion();
  const qc = useQueryClient();
  const detalle = useQuery({
    queryKey: ['config', 'usuario', usuario.id],
    queryFn: () =>
      api.get<{ asignaciones: Array<{ id: string; rol: string; empresa_id: string | null }> }>(
        `/usuarios/${usuario.id}`,
      ),
  });
  const [rol, setRol] = useState('');
  const [empresaId, setEmpresaId] = useState(empresa!.id);
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);
  const ejecutar = async (f: () => Promise<unknown>) => {
    setError(null);
    setTrabajando(true);
    try {
      await f();
      setMotivo('');
      await qc.invalidateQueries({ queryKey: ['config', 'usuario', usuario.id] });
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  };
  const nombreEmpresa = (id: string | null) =>
    id ? (empresas.find((e) => e.id === id)?.razon_social ?? id.slice(0, 8)) : 'todas';
  return (
    <Modal titulo={`Roles de ${usuario.nombre}`} alCerrar={alCerrar} ancho="ancho">
      <table className="grilla compacta" aria-label="Roles asignados">
        <thead>
          <tr>
            <th>Rol</th>
            <th>Empresa</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {(detalle.data?.asignaciones ?? []).map((a) => (
            <tr key={a.id}>
              <td>{a.rol}</td>
              <td>{nombreEmpresa(a.empresa_id)}</td>
              <td>
                <button
                  type="button"
                  className="boton-mini"
                  disabled={motivo.trim().length < 3 || trabajando}
                  title="Escribe el motivo abajo"
                  onClick={() =>
                    void ejecutar(() =>
                      api.delete(`/roles/asignaciones/${a.id}`, {
                        cuerpo: { motivo: motivo.trim() },
                      }),
                    )
                  }
                >
                  Quitar
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Rol</span>
          <select aria-label="Rol a asignar" value={rol} onChange={(e) => setRol(e.target.value)}>
            <option value="">—</option>
            {roles.map((r) => (
              <option key={r.codigo} value={r.codigo}>
                {r.nombre}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">Empresa</span>
          <select
            aria-label="Empresa del rol"
            value={empresaId}
            onChange={(e) => setEmpresaId(e.target.value)}
          >
            {empresas.map((e) => (
              <option key={e.id} value={e.id}>
                {e.razon_social}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">Motivo</span>
          <input aria-label="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </label>
        <button
          type="button"
          className="primario"
          disabled={!rol || motivo.trim().length < 3 || trabajando}
          onClick={() =>
            void ejecutar(() =>
              api.post(`/usuarios/${usuario.id}/roles`, {
                rolCodigo: rol,
                scope: { empresaId },
                motivo: motivo.trim(),
              }),
            )
          }
        >
          Asignar rol
        </button>
      </div>
      {error ? <PanelError error={error} /> : null}
    </Modal>
  );
}

export function Roles() {
  const permisos = useQuery({
    queryKey: ['config', 'permisos'],
    queryFn: () => api.get<Permiso[]>('/permisos'),
  });
  const [editar, setEditar] = useState<Rol | null>(null);
  return (
    <>
      <Mantenedor
        def={
          {
            etiqueta: 'Roles',
            claveConsulta: ['config', 'roles'],
            listar: () => api.get<Rol[]>('/roles'),
            clave: (r) => r.id,
            textoDe: (r) => `${r.codigo} ${r.nombre}`,
            columnas: [
              {
                id: 'c',
                titulo: 'Código',
                celda: (r) => <span className="mono">{r.codigo}</span>,
                ancho: '12rem',
              },
              { id: 'n', titulo: 'Nombre', celda: (r) => r.nombre },
              {
                id: 'p',
                titulo: 'Permisos',
                celda: (r) => r.permisos.length,
                alinear: 'der',
                ancho: '6rem',
              },
              {
                id: 'e',
                titulo: 'Editable',
                celda: (r) => (r.editable ? 'sí' : 'sistema'),
                ancho: '7rem',
              },
            ],
            campos: [
              {
                id: 'codigo',
                etiqueta: 'Código',
                requerido: true,
                soloCrear: true,
                patron: /^[a-z_]{3,40}$/,
                ayuda: 'minúsculas y guion bajo',
              },
              { id: 'nombre', etiqueta: 'Nombre', requerido: true },
              { id: 'descripcion', etiqueta: 'Descripción' },
            ],
            crear: (v, motivo) => ({
              ruta: '/roles',
              cuerpo: {
                codigo: v['codigo'],
                nombre: v['nombre'],
                ...(v['descripcion']?.trim() ? { descripcion: v['descripcion'].trim() } : {}),
                // Un rol nace con al menos un permiso (lectura); se completan con «Permisos».
                permisos: ['ventas.nota.ver'],
                motivo,
              },
            }),
            conMotivo: true,
            permisoCrear: 'organizacion.roles.gestionar',
            acciones: [
              {
                texto: 'Permisos',
                permiso: 'organizacion.roles.gestionar',
                visible: (r) => r.editable,
                abrir: (r) => setEditar(r),
              },
            ],
          } satisfies DefMantenedor<Rol>
        }
      />
      {editar && (
        <PermisosDeRol
          rol={editar}
          permisos={permisos.data ?? []}
          alCerrar={() => setEditar(null)}
        />
      )}
    </>
  );
}

function PermisosDeRol({
  rol,
  permisos,
  alCerrar,
}: {
  rol: Rol;
  permisos: Permiso[];
  alCerrar: () => void;
}) {
  const qc = useQueryClient();
  const [sel, setSel] = useState(new Set(rol.permisos));
  const [filtro, setFiltro] = useState('');
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const lista = permisos.filter(
    (p) => !filtro || `${p.codigo} ${p.descripcion}`.toLowerCase().includes(filtro.toLowerCase()),
  );
  const guardar = async () => {
    try {
      await api.put(`/roles/${rol.codigo}/permisos`, { permisos: [...sel], motivo: motivo.trim() });
      await qc.invalidateQueries({ queryKey: ['config', 'roles'] });
      alCerrar();
    } catch (e) {
      setError(e);
    }
  };
  return (
    <Modal titulo={`Permisos de ${rol.nombre}`} alCerrar={alCerrar} ancho="ancho">
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Buscar</span>
          <input
            aria-label="Buscar permiso"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
          />
        </label>
        <span>{sel.size} permisos</span>
      </div>
      <div style={{ maxHeight: '22rem', overflow: 'auto' }}>
        {lista.map((p) => (
          <label key={p.codigo} style={{ display: 'block' }}>
            <input
              type="checkbox"
              checked={sel.has(p.codigo)}
              onChange={() =>
                setSel((s) => {
                  const n = new Set(s);
                  if (n.has(p.codigo)) n.delete(p.codigo);
                  else n.add(p.codigo);
                  return n;
                })
              }
            />{' '}
            <span className="mono">{p.codigo}</span> · {p.descripcion}
            {p.sensible && <span className="insignia tono-aviso">sensible</span>}
          </label>
        ))}
      </div>
      <label className="campo">
        <span className="etiqueta">Motivo</span>
        <input aria-label="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      </label>
      {error ? <PanelError error={error} /> : null}
      <div className="acciones-modal">
        <button type="button" onClick={alCerrar}>
          Volver
        </button>
        <button
          type="button"
          className="primario"
          disabled={!sel.size || motivo.trim().length < 3}
          onClick={() => void guardar()}
        >
          Guardar permisos
        </button>
      </div>
    </Modal>
  );
}

export function Permisos() {
  return (
    <Mantenedor
      def={
        {
          etiqueta: 'Permisos',
          claveConsulta: ['config', 'permisos'],
          listar: () => api.get<Permiso[]>('/permisos'),
          clave: (p) => p.codigo,
          textoDe: (p) => `${p.codigo} ${p.descripcion} ${p.dominio}`,
          columnas: [
            {
              id: 'c',
              titulo: 'Permiso',
              celda: (p) => <span className="mono">{p.codigo}</span>,
              ancho: '18rem',
            },
            { id: 'd', titulo: 'Dominio', celda: (p) => p.dominio, ancho: '9rem' },
            { id: 'x', titulo: 'Descripción', celda: (p) => p.descripcion },
            { id: 's', titulo: 'Sensible', celda: (p) => (p.sensible ? 'sí' : ''), ancho: '6rem' },
            {
              id: 'm',
              titulo: 'Pide motivo',
              celda: (p) => (p.requiere_motivo ? 'sí' : ''),
              ancho: '7rem',
            },
          ],
          nota: (
            <p className="apagado">
              El catálogo de permisos lo define el sistema; se otorgan a través de los roles.
            </p>
          ),
        } satisfies DefMantenedor<Permiso>
      }
    />
  );
}
